import { useEffect, useRef, useState } from "react";
import { C } from "../../../constants/design";
import type { AvatarLayers } from "../types/leagueDuel";
import { LayeredCharacter } from "./LayeredCharacter";
import {
  DuelRotationState,
  DuelCombatEvent,
  DuelFighter,
  stepDuelRotation,
  duelRotationHead,
  DUEL_SPELL_DAMAGE_MULT,
} from "../engine/duelRotationCombat";
import {
  FONT,
  ST_BEVEL,
  ST_STYLE,
  ST_COMBAT_KEYFRAMES,
  ARENA_VS_W,
  DMG_OFFSET,
  FighterColumn,
  FighterStage,
  spellColor,
  spellIcon,
  spellName,
} from "../../stadium-trial/components/StadiumTrialScreen";
import {
  ROTATION_SPELL_DAMAGE_MULT,
  rotationSpellCost,
} from "../../stadium-trial/engine/pveCombatInteractive";
import { getSpell, spellComputedValues } from "../../stadium-trial/content/pveSpells";
import type { PveSpellId } from "../../stadium-trial/content/pveSpells";

// ─── Spell charge half (losange look, spell-scoped, mirrored for the enemy) ───
// The rotation engine banks gauge points (0..ROTATION_ENERGY_MAX, 10 per pip).
// Instead of a full gauge, we show ONLY the spell being charged: its icon +
// name (grey) and a row of losanges equal to THAT spell's cost, filling as
// energy banks toward it. When the charge completes — the spell fires and
// energy is consumed — the pips blink. Both fighters share ONE line: the
// player on the left, the enemy MIRRORED on the right. The losanges are
// ANCHORED at the center divider on both sides (first-filling pip nearest the
// center) so they never shift when a different spell — with a different pip
// count — comes up; the name sits at the outer edge.
// (RotationGauge, the filling bar, is kept intact and still used by the Stadium.)
const PIPS_PER_GAUGE = 10;

const DUEL_PIP_KEYFRAMES = `
  @keyframes duelPipBlink { 0%,100% { opacity: 1; filter: brightness(1); } 45% { opacity: 0.3; filter: brightness(2.2); } }
`;

function SpellChargeHalf({ spellId, energy, enemy = false }: {
  spellId: PveSpellId | null; energy: number; enemy?: boolean;
}) {
  const cost = spellId ? rotationSpellCost(spellId) : 0;
  const count = Math.max(1, Math.round(cost / PIPS_PER_GAUGE));
  const filled = Math.min(count, Math.floor(energy / PIPS_PER_GAUGE));
  const reached = cost > 0 && energy >= cost; // spell fires this frame → blink
  const size = enemy ? 12 : 14;
  // Name at the OUTER edge, pips hugging the center divider. The pip row fills
  // from the center out, so index 0 (first filled) must be the innermost pip:
  // player = pips right-anchored, filling right→left; enemy = mirror of that.
  return (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
      {/* Spell identity — icon + name, grey, pinned to the OUTER edge */}
      <span style={{ order: enemy ? 2 : 1, marginLeft: enemy ? "auto" : 0, font: `800 11px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: C.muted, display: "flex", flexDirection: enemy ? "row-reverse" : "row", alignItems: "center", gap: 6, flex: "none", maxWidth: 120, overflow: "hidden" }}>
        <span style={{ fontSize: 15, flex: "none" }}>{spellId ? spellIcon(spellId) : "—"}</span>
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{spellId ? spellName(spellId) : "—"}</span>
      </span>
      {/* Losanges = this spell's cost, anchored at the CENTER, filling outward.
          index 0 (first filled) is the innermost pip → row-reverse for player. */}
      <div style={{ order: enemy ? 1 : 2, marginLeft: enemy ? 0 : "auto", display: "flex", flexDirection: enemy ? "row" : "row-reverse", gap: 5, alignItems: "center", flex: "none" }}>
        {Array.from({ length: count }, (_, i) => {
          const isFilled = i < filled;
          return (
            <span key={i} style={{
              width: size, height: size, borderRadius: 4, transform: "rotate(45deg)",
              background: isFilled
                ? (enemy ? "linear-gradient(135deg,#FF8A90,#FF3B47)" : "linear-gradient(135deg,#7FE0FF,#4CC9F0)")
                : "rgba(255,255,255,0.07)",
              border: `1px solid ${isFilled ? (enemy ? "#FF3B47" : "#4CC9F0") : "rgba(255,255,255,0.14)"}`,
              boxShadow: isFilled ? (enemy ? "0 0 8px rgba(255,59,71,0.55)" : "0 0 8px rgba(76,201,240,0.55)") : "none",
              animation: reached ? "duelPipBlink 0.38s ease-in-out 2" : undefined,
              transition: "background 0.2s, box-shadow 0.2s",
            }} />
          );
        })}
      </div>
    </div>
  );
}

// ─── Duel 1v1 — rotation fight screen ─────────────────────────────────────────
// VISUAL CLONE of the Stadium Trial fight screen (same components, same
// animations, same layout) — the only difference is the opponent: a PvP ghost
// with its own rotation instead of a scripted enemy. Two SOLO fighters.
// The fight plays itself; this component reveals the engine's events one by
// one on a timer and renders the state. Nothing persisted here — the parent
// commits once via onFinish.

const EVENT_MS = 820;   // beat between two revealed events (≈ Stadium ENEMY_STEP_MS)
const TURN_MS = 620;    // pause before stepping the next fighter turn
const END_HOLD_MS = 1000;

export function DuelRotationMatch({ initial, homeLayers, awayLayers, onFinish }: {
  initial: DuelRotationState;
  homeLayers?: AvatarLayers;
  awayLayers?: AvatarLayers;
  homeAvatar?: string;
  awayAvatar?: string;
  onFinish: (final: DuelRotationState) => void;
}) {
  const [combat, setCombat] = useState<DuelRotationState>(initial);
  // Reveal cursor over combat.events — HP snapshots come from the shown event,
  // exactly like the Stadium frame-by-frame reveal.
  const [shownIdx, setShownIdx] = useState(-1);
  const [showSpells, setShowSpells] = useState(false);
  const finishedRef = useRef(false);

  // Driver — reveal pending events one by one, then step the next turn.
  useEffect(() => {
    if (finishedRef.current) return;
    const pending = combat.events.length - 1 - shownIdx;
    const t = setTimeout(() => {
      if (pending > 0) { setShownIdx((i) => i + 1); return; }
      if (!combat.over) { setCombat((c) => stepDuelRotation(c)); return; }
      // All events revealed and the fight is over → short hold, hand off once.
      setTimeout(() => {
        if (!finishedRef.current) { finishedRef.current = true; onFinish(combat); }
      }, END_HOLD_MS);
    }, pending > 0 ? EVENT_MS : TURN_MS);
    return () => clearTimeout(t);
  }, [combat, shownIdx]); // eslint-disable-line

  const last: DuelCombatEvent | null = shownIdx >= 0 ? combat.events[shownIdx] : null;
  const lastIsPlayer = last?.side === "attacker";
  // Displayed HP = snapshot of the SHOWN event (bars animate with the reveal).
  const shownAtkHp = last ? last.attackerHp : combat.attacker.maxHp;
  const shownDefHp = last ? last.defenderHp : combat.defender.maxHp;
  // Energy = snapshot of the SHOWN event — so the gauge visibly FILLS on the
  // auto hit (and reaches max) then DRAINS on the spell, like the Stadium.
  const shownAtkEnergy = last ? last.attackerEnergy : combat.attacker.energy;
  const shownDefEnergy = last ? last.defenderEnergy : combat.defender.energy;
  // Rotation head snapshot → the checkpoint tick reflects the spell being
  // charged at this event (green "reached" flash on the auto hit), not the
  // already-advanced live head.
  const shownAtkHead = last ? last.attackerHead : duelRotationHead(combat.attacker);
  const shownDefHead = last ? last.defenderHead : duelRotationHead(combat.defender);
  // Who is hit this frame: damage lands on the opposite side (echo included —
  // the echo event's side is the Vision caster, damage hits the other fighter).
  const hitSide = last && last.damage > 0 ? (last.side === "attacker" ? "defender" : "attacker") : null;
  const allRevealed = shownIdx >= combat.events.length - 1;
  const over = combat.over && allRevealed;

  const me = combat.attacker;
  const opp = combat.defender;

  // Active-effect badges — the invisible per-fighter states that persist UNTIL
  // consumed (Focus & Feint buff the caster's next offensive spell; the Vision
  // mark makes THIS fighter take echo on its next cast). Read from the live
  // fighter (same freshness as the shield shown in the HP bar) so a chip vanishes
  // exactly when the engine consumes the effect. Each set is drawn on its own
  // fighter's side.
  const effectsFor = (f: DuelFighter): { icon: string; label: string; color: string }[] => {
    const out: { icon: string; label: string; color: string }[] = [];
    if (f.focusBonusPercent > 0) out.push({ icon: spellIcon("focus"), label: `+${f.focusBonusPercent}%`, color: spellColor("focus") });
    if (f.offBalancePercent > 0) out.push({ icon: spellIcon("feint"), label: `+${f.offBalancePercent}%`, color: spellColor("feint") });
    if (f.echoIncomingPerEnergy > 0) out.push({ icon: spellIcon("vision"), label: `${f.echoIncomingPerEnergy}%/⚡`, color: spellColor("vision") });
    return out;
  };
  const attackerEffects = effectsFor(combat.attacker);
  const defenderEffects = effectsFor(combat.defender);

  // Skip — fast-forward the sim to the end and hand off once.
  const handleSkip = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    let s = combat;
    let guard = 0;
    while (!s.over && guard++ < 500) s = stepDuelRotation(s);
    onFinish(s);
  };

  return (
    // Fullscreen combat — same overlay as the Stadium fight (nav bar covered).
    <div style={{ position: "fixed", inset: 0, zIndex: 800, overflowY: "auto", background: "linear-gradient(180deg,#0b1322,#070d18)", fontFamily: FONT.ui }}>
    <div style={{ minHeight: "100%", maxWidth: 480, margin: "0 auto", boxSizing: "border-box", padding: "14px 16px calc(14px + env(safe-area-inset-bottom, 0px))", display: "flex", flexDirection: "column", gap: 14 }}>
      <style>{ST_STYLE}</style>
      <style>{ST_COMBAT_KEYFRAMES}</style>

      {/* Header — "DUEL · round" left, spells recap right (Stadium layout) */}
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 46 }}>
        <span style={{ font: `700 15px ${FONT.display}`, letterSpacing: 1.5, textTransform: "uppercase", color: C.text, whiteSpace: "nowrap" }}>
          {over ? (combat.winner === "attacker" ? "🏆 Victory" : "💀 Defeat") : `Duel · ${combat.round}`}
        </span>
        <button onClick={() => setShowSpells(true)} style={{ flex: "none", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.text, borderRadius: 8, padding: "5px 9px", font: `700 10px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap" }}>
          Spells
        </button>
      </div>

      {/* Arena — same two FighterColumns as the Stadium fight */}
      <div style={{ position: "relative", overflow: "hidden", borderRadius: 16, padding: "14px 10px 12px", background: "radial-gradient(300px 200px at 50% 120%, rgba(45,227,107,0.10), transparent 70%), linear-gradient(180deg,#0c1626,#070d18)", border: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "repeating-linear-gradient(0deg, rgba(76,201,240,0.05) 0 1px, transparent 1px 26px)" }} />
        <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-end", gap: 8 }}>
          <FighterColumn
            hp={Math.round(shownAtkHp)}
            maxHp={me.maxHp}
            shield={Math.round(me.shield)}
            tone="player"
            name={me.name}
            shaking={false}
            attacking={false}
            lungeDir="right"
            shakeIdx={shownIdx}
            dead={shownAtkHp <= 0}
          >
            <FighterStage
              side="ally"
              charShaking={hitSide === "attacker"}
              charAttacking={!!last && last.side === "attacker" && last.kind !== "echo"}
              shakeIdx={shownIdx}
            >
              <LayeredCharacter layers={homeLayers} glowColor={lastIsPlayer && hitSide ? "rgba(45,227,107,0.9)" : undefined} style={{ height: "100%", width: "auto" }} />
            </FighterStage>
          </FighterColumn>

          {/* Central zone — VS marker + floating cues for the LAST event */}
          <div style={{ width: ARENA_VS_W, flexShrink: 0, alignSelf: "center", position: "relative", display: "flex", justifyContent: "center" }}>
            <div style={{ font: `700 13px ${FONT.display}`, letterSpacing: 1, color: "#4CC9F0", textShadow: "0 0 10px rgba(76,201,240,0.6)" }}>VS</div>
            {/* Projectile — flies from the acting fighter toward the target */}
            {last && last.damage > 0 && (
              <div
                key={`proj-${shownIdx}`}
                style={{
                  position: "absolute", top: "50%", left: "50%", marginTop: -10, marginLeft: -10,
                  width: 20, height: 20, fontSize: 16, lineHeight: "20px", textAlign: "center",
                  animation: hitSide === "defender" ? "stProjFlyR 0.22s ease-out both" : "stProjFlyL 0.22s ease-out both",
                  pointerEvents: "none", zIndex: 10,
                }}
              >
                ⚽
              </div>
            )}
            {last && last.damage > 0 && (
              <div
                key={`dmg-${shownIdx}`}
                style={{
                  position: "absolute", top: -26,
                  ...(hitSide === "defender" ? { left: "50%", marginLeft: DMG_OFFSET } : { right: "50%", marginRight: DMG_OFFSET }),
                  font: `700 ${last.crit ? 22 : 18}px ${FONT.display}`,
                  color: last.crit ? C.gold : C.live,
                  animation: "stDmgPop 0.85s ease-out both",
                  pointerEvents: "none", whiteSpace: "nowrap", textAlign: "center",
                }}
              >
                {last.crit && <div style={{ font: `700 10px ${FONT.label}`, letterSpacing: 1, color: C.gold }}>CRIT</div>}
                -{last.damage}
              </div>
            )}
            {/* Spell name cue — above the damage, on the caster's side */}
            {last && last.spellId && (
              <div key={`sp-${shownIdx}`} style={{ position: "absolute", top: -52, ...(lastIsPlayer ? { left: "50%", marginLeft: DMG_OFFSET } : { right: "50%", marginRight: DMG_OFFSET }), fontSize: 12, fontWeight: 900, color: spellColor(last.spellId), animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
                {spellIcon(last.spellId)} {spellName(last.spellId)}
              </div>
            )}
            {/* Heal / self-damage / shield cues on the caster's side */}
            {last?.heal ? (
              <div key={`heal-${shownIdx}`} style={{ position: "absolute", top: -26, ...(lastIsPlayer ? { right: "50%", marginRight: DMG_OFFSET } : { left: "50%", marginLeft: DMG_OFFSET }), fontSize: 16, fontWeight: 900, color: C.turf, animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
                +{last.heal}💚
              </div>
            ) : null}
            {last?.selfDamage ? (
              <div key={`self-${shownIdx}`} style={{ position: "absolute", top: -26, ...(lastIsPlayer ? { right: "50%", marginRight: DMG_OFFSET } : { left: "50%", marginLeft: DMG_OFFSET }), fontSize: 15, fontWeight: 900, color: C.live, animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
                -{last.selfDamage}🩸
              </div>
            ) : null}
            {last?.shieldGained ? (
              <div key={`sh-${shownIdx}`} style={{ position: "absolute", top: -26, ...(lastIsPlayer ? { right: "50%", marginRight: DMG_OFFSET } : { left: "50%", marginLeft: DMG_OFFSET }), fontSize: 15, fontWeight: 900, color: "#7FE0FF", animation: "stDmgPop 0.85s ease-out both", pointerEvents: "none", whiteSpace: "nowrap" }}>
                +{last.shieldGained}🛡
              </div>
            ) : null}
          </div>

          <FighterColumn
            hp={Math.round(shownDefHp)}
            maxHp={opp.maxHp}
            shield={Math.round(opp.shield)}
            tone="enemy"
            name={opp.name}
            shaking={false}
            attacking={false}
            lungeDir="left"
            shakeIdx={shownIdx}
            dead={shownDefHp <= 0}
          >
            <FighterStage
              side="enemy"
              charShaking={hitSide === "defender"}
              charAttacking={!!last && last.side === "defender" && last.kind !== "echo"}
              shakeIdx={shownIdx}
            >
              <LayeredCharacter layers={awayLayers} mirror style={{ height: "100%", width: "auto" }} />
            </FighterStage>
          </FighterColumn>
        </div>
      </div>

      {/* Active-effect rail — persistent buff/mark chips, each on its fighter's
          side (left = player, right = enemy), until the engine consumes them. */}
      {(attackerEffects.length > 0 || defenderEffects.length > 0) && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginTop: -4 }}>
          {([attackerEffects, defenderEffects] as const).map((fx, side) => (
            <div key={side} style={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", gap: 6, justifyContent: side === 0 ? "flex-start" : "flex-end" }}>
              {fx.map((e, i) => (
                <span key={i} style={{
                  display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px",
                  borderRadius: 8, background: `${e.color}1c`, border: `1px solid ${e.color}66`,
                  boxShadow: `0 0 8px ${e.color}33`, font: `700 10px ${FONT.label}`,
                  letterSpacing: 0.5, color: e.color, whiteSpace: "nowrap",
                }}>
                  <span style={{ fontSize: 12 }}>{e.icon}</span>{e.label}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Charge line — both fighters on ONE line, mirrored: the spell each is
          charging (name grey + losanges = that spell's cost). Player fills
          left→center, enemy fills right→center; the pips blink when it fires. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, background: "linear-gradient(180deg,#121a2b,#0c1322)", borderRadius: 14, padding: "11px 14px", border: "1px solid rgba(255,255,255,0.08)", clipPath: ST_BEVEL }}>
        <style>{DUEL_PIP_KEYFRAMES}</style>
        <SpellChargeHalf spellId={shownAtkHead} energy={shownAtkEnergy} />
        <div style={{ flex: "none", width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.1)" }} />
        <SpellChargeHalf enemy spellId={shownDefHead} energy={shownDefEnergy} />
      </div>

      <div style={{ flex: 1 }} />

      {/* MY rotation bar — read-only timeline in ROTATION order, head glowing,
          numbers = firing order (same bar as the Stadium rotation mode). */}
      <div style={{ display: "flex", gap: 8 }}>
        {me.rotation.map((id, slotIdx) => {
          const def = getSpell(id);
          if (!def) return null;
          const cd = me.cooldowns[id] ?? 0;
          const isHead = slotIdx === me.rotationIndex % me.rotation.length;
          const col = def.color;
          return (
            <div
              key={`${id}-${slotIdx}`}
              style={{
                flex: 1, minWidth: 0, position: "relative",
                borderRadius: 12, padding: "10px 4px 8px",
                background: isHead ? `linear-gradient(180deg,${col}20,${col}08)` : "rgba(255,255,255,0.03)",
                border: `1.5px solid ${isHead ? col + "88" : "rgba(255,255,255,0.08)"}`,
                boxShadow: isHead ? `0 0 14px ${col}33` : "none",
                opacity: isHead ? 1 : 0.45,
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                transition: "opacity 0.15s, box-shadow 0.15s",
              }}
            >
              <span style={{ position: "absolute", top: 4, left: 6, font: `800 10px ${FONT.display}`, color: isHead ? col : C.muted }}>{slotIdx + 1}</span>
              <span style={{ fontSize: 21, lineHeight: 1 }}>{def.icon}</span>
              <span style={{ font: `700 9px ${FONT.label}`, letterSpacing: 0.4, color: isHead ? col : C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{spellName(id)}</span>
              <span style={{ font: `800 10px ${FONT.display}`, color: "#fff" }}>⚡{rotationSpellCost(id)}</span>
              {(() => {
                const v = spellComputedValues(id, me.stats.physique, me.maxHp, 1);
                const dmg = v.damage != null ? Math.round(v.damage * ROTATION_SPELL_DAMAGE_MULT * DUEL_SPELL_DAMAGE_MULT) : null;
                return dmg != null ? <span style={{ font: `700 9px ${FONT.display}`, color: "#9fb0c8" }}>≈{dmg} dmg</span> : null;
              })()}
              {cd > 0 && (
                <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(7,13,24,0.62)", borderRadius: 12, font: `800 13px ${FONT.display}`, color: C.text }}>
                  {cd}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Skip — discreet secondary action, hidden once the fight is over */}
      {!over && (
        <button
          onClick={handleSkip}
          style={{
            alignSelf: "center", marginTop: 2,
            border: "1px solid rgba(255,255,255,0.10)", background: "transparent",
            color: C.muted, borderRadius: 8, padding: "6px 16px",
            font: `700 10px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase",
            cursor: "pointer", opacity: 0.6,
          }}
        >
          Skip
        </button>
      )}
      </div>

      {/* Spells recap — my rotation + the opponent's (both visible in PvP) */}
      {showSpells && (
        <div onClick={() => setShowSpells(false)} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto", background: "linear-gradient(180deg,#141d31,#0b1120)", borderTopLeftRadius: 22, borderTopRightRadius: 22, borderTop: "1px solid rgba(255,255,255,0.1)", padding: "18px 16px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            {([[`⚔️ ${me.name}`, me], [`🛡️ ${opp.name}`, opp]] as [string, DuelFighter][]).map(([title, f]) => (
              <div key={title} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ font: `700 15px ${FONT.display}`, letterSpacing: 1, textTransform: "uppercase", color: C.text, textAlign: "center" }}>{title}</div>
                {f.rotation.map((id, i) => {
                  const def = getSpell(id);
                  if (!def) return null;
                  return (
                    <div key={`${id}-${i}`} style={{ display: "flex", gap: 11, alignItems: "flex-start", background: `linear-gradient(180deg,${def.color}12,${def.color}04)`, border: `1px solid ${def.color}44`, borderRadius: 13, padding: "11px 12px" }}>
                      <span style={{ flex: "none", width: 42, height: 42, display: "grid", placeItems: "center", borderRadius: 10, fontSize: 22, background: `${def.color}22`, border: `1px solid ${def.color}55` }}>{def.icon}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ font: `700 12px ${FONT.display}`, color: C.muted }}>{i + 1}.</span>
                          <span style={{ font: `700 14px ${FONT.display}`, color: def.color }}>{spellName(id)}</span>
                          <span style={{ font: `800 11px ${FONT.display}`, color: "#fff" }}>⚡{rotationSpellCost(id)}</span>
                        </div>
                        <div style={{ font: `500 12px ${FONT.ui}`, color: C.muted, lineHeight: 1.35, marginTop: 3 }}>{def.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <button onClick={() => setShowSpells(false)} style={{ marginTop: 4, width: "100%", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.05)", color: C.text, borderRadius: 10, padding: "11px 0", font: `700 12px ${FONT.label}`, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
